'use client'

import { useState, useRef, useEffect, useId } from 'react'
import { useLocale } from '@/components/LocaleProvider'
import { LOCALES, type Locale } from '@/lib/i18n'

const ChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m6 9 6 6 6-6" />
  </svg>
)

const Check = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

const FLAG_SM = { w: 12, h: 9 }
const FLAG_MD = { w: 14, h: 10 }

function FlagIcon({ locale, size = 'sm' }: { locale: Locale; size?: 'sm' | 'md' }) {
  const { w, h } = size === 'md' ? FLAG_MD : FLAG_SM
  const vb = '0 0 30 20'
  return (
    <span
      className="inline-block shrink-0 overflow-hidden rounded-[1px]"
      style={{ width: w, height: h, minWidth: w, minHeight: h }}
      aria-hidden
    >
      {locale === 'en-GB' && (
        <svg width={w} height={h} viewBox={vb} className="block" preserveAspectRatio="xMidYMid meet">
          <rect width="30" height="20" fill="#012169" />
          <path d="M0,0 L30,20 M30,0 L0,20" stroke="#fff" strokeWidth="2.5" />
          <path d="M0,0 L30,20 M30,0 L0,20" stroke="#C8102E" strokeWidth="1.5" />
          <path d="M15,0 v20 M0,10 h30" stroke="#fff" strokeWidth="4" />
          <path d="M15,0 v20 M0,10 h30" stroke="#C8102E" strokeWidth="2.5" />
        </svg>
      )}
      {locale === 'en-US' && (
        <svg width={w} height={h} viewBox="0 0 30 21" className="block" preserveAspectRatio="xMidYMid meet">
          {/* 13 stripes in 0–20, viewBox 21 tall so bottom isn't clipped */}
          <rect x="0" y="0" width="30" height="20" fill="#B22234" />
          <rect x="0" y={20/13} width="30" height={20/13} fill="#fff" />
          <rect x="0" y={3*20/13} width="30" height={20/13} fill="#fff" />
          <rect x="0" y={5*20/13} width="30" height={20/13} fill="#fff" />
          <rect x="0" y={7*20/13} width="30" height={20/13} fill="#fff" />
          <rect x="0" y={9*20/13} width="30" height={20/13} fill="#fff" />
          <rect x="0" y={11*20/13} width="30" height={20/13} fill="#fff" />
          <rect x="0" y="0" width="12" height={7*20/13} fill="#3C3B6E" />
          {[0, 1, 2].map((row) =>
            [0, 1, 2, 3, 4].map((col) => (
              <circle key={`${row}-${col}`} cx={2.2 + col * 2.4} cy={1.1 + row * 2} r="0.5" fill="#fff" />
            ))
          )}
        </svg>
      )}
      {locale === 'nl' && (
        <svg width={w} height={h} viewBox={vb} className="block" preserveAspectRatio="xMidYMid meet">
          <rect width="30" height="6.67" fill="#AE1C28" />
          <rect width="30" height="6.67" y="6.67" fill="#fff" />
          <rect width="30" height="6.66" y="13.34" fill="#21468B" />
        </svg>
      )}
    </span>
  )
}

type LanguageSelectProps = { placement?: 'up' | 'down'; inline?: boolean }

export default function LanguageSelect({ placement = 'down', inline = false }: LanguageSelectProps) {
  const { locale, setLocale, t } = useLocale()
  const triggerId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLUListElement>(null)
  const selectedOptionRef = useRef<HTMLLIElement>(null)

  const current = LOCALES.find((l) => l.value === locale) ?? LOCALES[0]

  useEffect(() => {
    if (!isOpen) return
    selectedOptionRef.current?.focus({ preventScroll: true })
  }, [isOpen])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false)
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }
    const options = listboxRef.current?.querySelectorAll('[role="option"]') ?? []
    const currentIndex = LOCALES.findIndex((l) => l.value === locale)
    if (e.key === 'ArrowDown' && currentIndex < options.length - 1) {
      e.preventDefault()
      setLocale(LOCALES[currentIndex + 1].value)
      ;(options[currentIndex + 1] as HTMLElement)?.focus()
    } else if (e.key === 'ArrowUp' && currentIndex > 0) {
      e.preventDefault()
      setLocale(LOCALES[currentIndex - 1].value)
      ;(options[currentIndex - 1] as HTMLElement)?.focus()
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setIsOpen(false)
    }
  }

  function selectOption(value: Locale) {
    setLocale(value)
    setIsOpen(false)
  }

  return (
    <div className="flex items-center shrink-0">
      <div ref={containerRef} className="relative flex flex-col">
        <button
          type="button"
          onClick={() => setIsOpen((o) => !o)}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={t('locale.language')}
          id={triggerId}
          title={current.label}
          className={`flex items-center justify-center gap-0.5 rounded-lg text-foreground/70 transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
            inline ? 'min-h-0 h-7 w-8 px-1' : 'min-h-[44px] min-w-[44px] gap-1'
          }`}
        >
          <FlagIcon locale={current.value} size={inline ? 'sm' : 'md'} />
          <span className="text-foreground/55" aria-hidden>
            <ChevronDown />
          </span>
        </button>

        {isOpen && (
          <ul
            ref={listboxRef}
            role="listbox"
            aria-labelledby={triggerId}
            className={`absolute left-0 z-50 min-w-[9.5rem] max-h-[280px] overflow-auto rounded-md border border-border bg-popover/95 py-1 shadow-md backdrop-blur-sm focus:outline-none ${
              placement === 'up' ? 'bottom-full top-auto mb-1.5 mt-0' : 'top-full mt-3'
            }`}
          >
            {LOCALES.map((option) => (
              <li
                key={option.value}
                ref={option.value === locale ? selectedOptionRef : undefined}
                role="option"
                tabIndex={option.value === locale ? 0 : -1}
                aria-selected={option.value === locale}
                onClick={() => selectOption(option.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    selectOption(option.value)
                  }
                }}
                className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs text-popover-foreground transition-colors hover:bg-accent/80 hover:text-accent-foreground focus:bg-accent/80 focus:text-accent-foreground focus:outline-none data-[selected]:bg-accent/60 data-[selected]:text-accent-foreground"
                data-selected={option.value === locale || undefined}
              >
                <FlagIcon locale={option.value} size="md" />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.value === locale && (
                  <span className="shrink-0 text-primary">
                    <Check />
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
